import { Formik } from 'formik'
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, Loading, Typography } from '../../../../components'
import ErrorMessage from '../../../../components/Form/ErrorMessage'
import { Form, FormBody, FormFooter } from '../../../../components/Form/styles'
import useActions from '../../../../hooks/useActions'
import { signUpActions } from '../../../../redux/ducks/signUp'
import { InputContainer } from '../../styles'
import { useModal } from '../../../../hooks/useModal'
import { MessageModal } from '../../../../modals/MessageModal'
import * as Yup from 'yup'
import * as S from './styles'
import Input from '../../../../components/Form/Input'
import printer from '../../../../assets/Icons/printer.svg'
import holdingPhone from '../../../../assets/Icons/holdingPhone.svg'
import copyToClipboard from '../../../../utils/copyToClipboard'
import { openLinkOfPDFInANewBrowserTab } from '../../../../helpers/openPDFOnBrowser'
import { dateTypes } from '../../../../constants/date'
import { PaymentMethod } from '../../../../constants/order'
import LocalStorageService from '../../../../services/LocalStorageService'
import { SuccessMessage } from '../../../../components/Form/SuccessMessage'
import { ModalBodyContainer } from '../../../../modals/styles'
import { formatCentsToReal } from '../../../../helpers/formatCentsToReal'
import dayjs from '../../../../helpers/dayjs'
import { useValidateCoupon } from '../helpers'
import ecbrApi from '../../../../services/ecbrApi'
import { ConfigurationsKeys } from '../../../../constants/adminEcbrConfigurations'
import { useQuery } from 'react-query'

const BankSlipForm = () => {
  const user = LocalStorageService.get('user') || {}
  const { setStep4, nextStep, prevStep, pay, payWithExistingUser, setError } =
    useActions(signUpActions)
  const { error, step3, step4, step5 } = useSelector((state) => state.signUp)
  const [renderMessageModal, openMessageModal] = useModal(MessageModal)
  const [clipboardBtnText, setClipboardBtnText] = useState('Copiar Código')
  const [discountPercentage, setDiscountPercentage] = React.useState(0)

  const { success, bankSlip } = step5?.purchase?.order ?? {}
  const { productSelected } = step3

  const formattedAmount = useMemo(
    () =>
      formatCentsToReal(
        productSelected.amount - productSelected.amount * discountPercentage
      ),
    [productSelected.amount, discountPercentage]
  )

  const openModalBankSlipInfo = useCallback(
    () =>
      openMessageModal({
        title: 'Importante',
        body: (
          <ModalBodyContainer>
            <Typography type="text" color="#7D7987">
              • No período de 30 dias, se houver cinco ou mais boletos bancários
              de um mesmo cliente, não pagos, poderá ocorrer bloqueio temporário
              desta forma de quitação, por até 30 dias.
            </Typography>
            <Typography type="text" color="#7D7987">
              • Caso o seu computador tenha um programa anti pop-up, será
              preciso desativá-lo antes de finalizar sua compra e imprimir o
              boleto ou pagar pelo internet banking;
            </Typography>
            <Typography type="text" color="#7D7987">
              • Não faça depósito ou transferência entre contas. O boleto não é
              enviado pelos Correios. Imprima-o e pague-o no banco ou pela
              internet;
            </Typography>
            <Typography type="text" color="#7D7987">
              • Se o boleto não for pago até a data de vencimento, o pedido será
              automaticamente cancelado;
            </Typography>
          </ModalBodyContainer>
        )
      }),
    [openMessageModal]
  )

  useEffect(() => {
    openModalBankSlipInfo()
  }, [openModalBankSlipInfo])

  const { data: bankSlipDueDays, isLoading: isLoadingBankSlipDays } = useQuery(
    'configs_bank_slip',
    async () => {
      const { data } = await ecbrApi.configs.getByKey(
        ConfigurationsKeys.BANK_SLIP_DUE_DAYS
      )
      const { value, ...rest } = data
      return {
        value: value ? parseInt(value) : value,
        ...rest
      }
    },
    {
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError('Não foi possível carregar configurações do boleto')
      }
    }
  )

  const onSubmit = async ({ coupon, ..._values }, { setSubmitting }) => {
    setSubmitting(true)

    await setStep4({
      ...step4,
      coupon
    })

    // If the user is not empty, it means that the user is already logged in and have an account. So, it means that the payment is to buy another certificate for another brand of the same user company.
    if (user?.id) {
      await payWithExistingUser(PaymentMethod.BANK_SLIP, user)
    } else {
      await pay(PaymentMethod.BANK_SLIP)
    }

    setSubmitting(false)
  }

  const handleClickCopyToClipboard = () => {
    setClipboardBtnText('Copiado!')
    copyToClipboard(bankSlip.line)
    setTimeout(() => {
      setClipboardBtnText('Copiar Código')
    }, 2000)
  }

  const handleValidateCoupon = useValidateCoupon(
    setDiscountPercentage,
    setError,
    discountPercentage
  )

  const formSchema = useMemo(
    () =>
      Yup.object().shape({
        coupon: Yup.string()
          .optional()
          .nullable()
          .test('validateCoupon', 'Cupom inválido', handleValidateCoupon)
      }),
    [handleValidateCoupon]
  )

  const initialValues = {
    dueDate: dayjs()
      .add(Number(bankSlipDueDays?.value ?? 0), 'day')
      .format(dateTypes.date),
    bankSlipNumber: '',
    bankSlipValue: '',
    bankSlipHolderName: '',
    bankSlipHolderDocument: '',
    coupon: ''
  }

  return (
    <InputContainer>
      {renderMessageModal}
      {isLoadingBankSlipDays && <Loading />}
      {!isLoadingBankSlipDays && (
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={formSchema}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <FormBody type="flex" centered>
                {success ? (
                  <>
                    <Typography
                      type="text"
                      color="#7D7987"
                      spacing="0 0 16px 0"
                      align="center"
                      weight="bold"
                    >
                      Sua compra foi realizada e será processada após o
                      pagamento do Boleto
                    </Typography>
                    <S.IconMessageContainer>
                      <S.IconMessage src={printer} alt="printer" />
                      <Typography type="text" color="#7D7987">
                        Imprima o boleto e pague no banco
                      </Typography>
                    </S.IconMessageContainer>
                    <S.IconMessageContainer>
                      <S.IconMessage src={holdingPhone} alt="holdingPhone" />
                      <Typography type="text" color="#7D7987">
                        Ou pague através da internet utilizando o código de
                        barras do boleto
                      </Typography>
                    </S.IconMessageContainer>
                  </>
                ) : (
                  <>
                    <Typography
                      type="text"
                      color="#7D7987"
                      spacing="0 0 16px 0"
                    >
                      Pagamento com boleto bancário
                    </Typography>
                    <Input
                      name="coupon"
                      label="Cupom de desconto"
                      showValidIcon
                      textTransform="uppercase"
                    />
                    {discountPercentage > 0 && (
                      <SuccessMessage>
                        {`Cupom de desconto aplicado: ${
                          discountPercentage * 100
                        }%`}
                      </SuccessMessage>
                    )}
                  </>
                )}
                <S.BoxCardBankSlip>
                  <S.ValueContainer>
                    <Typography type="text" color="#151515">
                      Valor Total:
                    </Typography>
                    <Typography type="text" color="#151515">
                      {discountPercentage > 0 ? (
                        <>
                          de{' '}
                          <strike>
                            {formatCentsToReal(productSelected.amount)}
                          </strike>{' '}
                          por <strong>{formattedAmount}</strong>
                        </>
                      ) : (
                        formattedAmount
                      )}
                    </Typography>
                  </S.ValueContainer>
                  <Typography
                    type="subTitle"
                    color="#7D7987"
                    spacing="16px 0 0"
                  >
                    Vencimento:{' '}
                    {bankSlipDueDays?.value
                      ? dayjs(values.dueDate, dateTypes.date).format(
                          dateTypes.dateBR
                        )
                      : '--/--/----'}
                  </Typography>
                  {bankSlip?.line && (
                    <>
                      <Typography
                        type="label"
                        color="#151515"
                        spacing="16px 0 0"
                      >
                        Código do boleto:
                      </Typography>
                      <Typography type="text" color="#7D7987">
                        {bankSlip.line}
                      </Typography>
                      <Button
                        text={clipboardBtnText}
                        type="button"
                        variant="textLink"
                        color="var(--theme-primary)"
                        onClick={handleClickCopyToClipboard}
                        disabled={isSubmitting}
                      />
                    </>
                  )}
                </S.BoxCardBankSlip>
                {error && <ErrorMessage width="100%">{error}</ErrorMessage>}
              </FormBody>
              <FormFooter>
                {success ? (
                  <>
                    <Button
                      text="Gerar PDF"
                      type="button"
                      variant="outlined"
                      onClick={() =>
                        openLinkOfPDFInANewBrowserTab(bankSlip.pdf)
                      }
                      width="200px"
                      disabled={isSubmitting}
                    />
                    <Button
                      text="FINALIZAR"
                      type="button"
                      variant="primary"
                      onClick={() => nextStep()}
                      width="200px"
                      disabled={isSubmitting}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      text="VOLTAR"
                      type="button"
                      variant="outlined"
                      width="200px"
                      onClick={prevStep}
                    />
                    <Button
                      text="Gerar boleto"
                      type="submit"
                      variant="primary"
                      width="200px"
                      disabled={isSubmitting}
                    />
                  </>
                )}
              </FormFooter>
              <Typography
                type="label"
                color="#151515"
                spacing="16px 0 0"
                align="center"
                weight="bold"
                onClick={() => openModalBankSlipInfo()}
              >
                Confira informações importantes
              </Typography>
            </Form>
          )}
        </Formik>
      )}
    </InputContainer>
  )
}

export default BankSlipForm
